import { clearToken, getToken, setStoredUser, setToken } from '@/lib/token'

const API_BASE = ''

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: string,
  ) {
    super(message)
  }
}

async function _fetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers as Record<string, string> || {}),
  }

  const resp = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  })

  if (resp.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'Unauthorized')
  }

  if (!resp.ok) {
    let detail = ''
    try {
      const body = await resp.json()
      detail = body.detail || JSON.stringify(body)
    } catch {
      detail = await resp.text()
    }
    throw new ApiError(resp.status, `HTTP ${resp.status}`, detail)
  }

  return resp
}

export async function getJson<T>(path: string): Promise<T> {
  const resp = await _fetch(path)
  return resp.json() as Promise<T>
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await _fetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return resp.json() as Promise<T>
}

export async function putJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await _fetch(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
  return resp.json() as Promise<T>
}

export async function postEmpty(path: string, body: unknown = {}): Promise<void> {
  await _fetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function deleteReq(path: string): Promise<void> {
  await _fetch(path, { method: 'DELETE' })
}

export async function login(email: string, password: string) {
  const resp = await _fetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  const data = await resp.json()
  if (data.status === 'authenticated') {
    setToken(data.access_token)
    setStoredUser(data.user)
  }
  return data
}

export async function me() {
  return getJson<{ user: { id: number; email: string; role: string } }>('/auth/me')
}

export async function uploadImage(sessionId: string, file: File): Promise<{ path: string }> {
  const token = getToken()
  const formData = new FormData()
  formData.append('file', file)

  const resp = await fetch(`/v1/sessions/${sessionId}/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  })

  if (!resp.ok) {
    let detail = ''
    try {
      const body = await resp.json()
      detail = body.detail || JSON.stringify(body)
    } catch {
      detail = await resp.text()
    }
    throw new ApiError(resp.status, `Upload failed: HTTP ${resp.status}`, detail)
  }

  return resp.json() as Promise<{ path: string }>
}
